import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * SuggestionsPanel — left-hand panel in CandidatePreviewPanel that surfaces
 * AI-proposed profile updates read out of the candidate's notes and call
 * transcripts.
 *
 * The point: recruiters write real intel into notes ("she'd move to NYC for
 * the right role") that never reaches the structured widget fields, so
 * filters and reports stay wrong. This reads what's already written and
 * proposes the matching field change.
 *
 * Nothing applies automatically. Each card shows the proposed change, the
 * note it came from, and a verbatim quote, then waits for Apply or Dismiss.
 *
 * Two kinds of suggestion:
 *   - applyable  → writes straight to client_candidate_data on Apply
 *   - review     → positions/education/tags, which live behind different
 *                  endpoints; these show "Got it" and just acknowledge,
 *                  pointing the user at the right widget instead of
 *                  pretending to apply.
 */

// Full class strings, not interpolated fragments. Tailwind's JIT scans source
// text literally, so `bg-${color}-50` would be purged from the build and the
// badge would render unstyled.
const WIDGET_META = {
  tracking:     { label: 'Candidate Tracking', badge: 'bg-emerald-50 text-emerald-700' },
  compensation: { label: 'Compensation',       badge: 'bg-green-50 text-green-700' },
  contacts:     { label: 'Contacts & Links',   badge: 'bg-purple-50 text-purple-700' },
  positions:    { label: 'Positions',          badge: 'bg-amber-50 text-amber-700' },
  education:    { label: 'Education',          badge: 'bg-amber-50 text-amber-700' },
  tags:         { label: 'Tags',               badge: 'bg-amber-50 text-amber-700' },
  relationships:{ label: 'Relationships',      badge: 'bg-amber-50 text-amber-700' },
};

const CONFIDENCE_STYLES = {
  high:   'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  low:    'bg-gray-100 text-gray-600',
};

const SuggestionsPanel = ({ candidateId, onClose, onApplied, onCountChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  // Budget exhaustion is a distinct state from an error — it isn't a
  // failure, retrying won't help, and the message should say when it clears.
  const [capped, setCapped] = useState(null);
  const [notDeployed, setNotDeployed] = useState(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  });

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/candidates/${candidateId}/suggestions?status=pending`,
        { headers: authHeaders() }
      );
      // A 404 here means the server half of this feature isn't deployed yet
      // (routes/suggestions.js + its mount in server.js). That's a deployment
      // state, not a user-facing error, so fall back to the empty state and
      // say so plainly rather than showing a red failure box.
      if (res.status === 404) {
        setSuggestions([]);
        setNotDeployed(true);
        onCountChange?.(0);
        return;
      }
      if (!res.ok) throw new Error('Could not load suggestions.');
      setNotDeployed(false);
      const list = await res.json();
      setSuggestions(list);
      onCountChange?.(Array.isArray(list) ? list.length : 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [candidateId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/candidates/${candidateId}/suggestions/generate`,
        { method: 'POST', headers: authHeaders() }
      );
      if (res.status === 404) { setNotDeployed(true); return; }
      const body = await res.json().catch(() => ({}));
      if (res.status === 402 || body.capped) {
        setCapped(body.error || 'Suggestions spend limit reached.');
        return;
      }
      if (!res.ok) throw new Error(body.error || 'Could not generate suggestions.');
      setCapped(null);
      setSuggestions(body.suggestions || []);
      setLastRun({
        generated: body.generated || 0,
        reason: body.reason,
        at: new Date(),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const resolve = async (id, action) => {
    setBusyId(id);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/candidates/${candidateId}/suggestions/${id}/${action}`,
        { method: 'POST', headers: authHeaders() }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Could not ${action} suggestion.`);
      setSuggestions(prev => prev.filter(s => s.id !== id));
      // Only refresh the profile when something actually changed on it.
      if (action === 'accept' && body.applied && onApplied) onApplied();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const dismissAll = async () => {
    setBusyId('all');
    try {
      await fetch(
        `${API_BASE_URL}/api/candidates/${candidateId}/suggestions/dismiss-all`,
        { method: 'POST', headers: authHeaders() }
      );
      setSuggestions([]);
    } catch {
      setError('Could not dismiss all.');
    } finally {
      setBusyId(null);
    }
  };

  const formatValue = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    let parsed = v;
    if (typeof v === 'string') {
      try { parsed = JSON.parse(v); } catch { return v; }
    }
    if (Array.isArray(parsed)) {
      if (!parsed.length) return '—';
      return parsed
        .map(x => (typeof x === 'object' && x !== null
          ? [x.city, x.state, x.country].filter(Boolean).join(', ') || JSON.stringify(x)
          : String(x)))
        .join(', ');
    }
    if (typeof parsed === 'boolean') return parsed ? 'Yes' : 'No';
    if (typeof parsed === 'object') return JSON.stringify(parsed);
    return String(parsed);
  };

  return (
    <div className="bg-white flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 h-9 px-3 border-b border-gray-200 bg-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
          <span className="text-xs font-semibold text-gray-800">Suggestions</span>
          {suggestions.length > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded-full">
              {suggestions.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {suggestions.length > 1 && (
            <button
              onClick={dismissAll}
              disabled={busyId === 'all'}
              className="text-[10px] text-gray-400 hover:text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-100"
            >
              Dismiss all
            </button>
          )}
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Run bar */}
      <div className="flex-shrink-0 px-3 py-2 border-b bg-gray-50">
        <button
          onClick={generate}
          disabled={generating || !!capped || notDeployed}
          className="w-full py-1.5 text-xs font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-[1.5px] border-white border-t-transparent" />
              Reading notes…
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Scan notes for updates
            </>
          )}
        </button>
        <p className="text-[10px] text-gray-400 mt-1 leading-snug">
          Reads this candidate's notes and call transcripts, then proposes profile changes. Nothing is saved until you apply it.
        </p>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {capped && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 flex gap-2">
            <svg className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.71-3.03l-6.93-11a2 2 0 00-3.42 0l-6.93 11A2 2 0 005.07 19z" />
            </svg>
            <div>
              <p className="text-[11px] text-amber-800 font-medium">{capped}</p>
              <p className="text-[10px] text-amber-700 mt-0.5">
                Existing suggestions below are unaffected. An admin can adjust limits under User Management → AI Usage.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-2">
            <p className="text-[11px] text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400 mx-auto" />
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-8 px-3">
            <svg className="w-8 h-8 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {notDeployed && (
              <p className="text-xs text-gray-500 mb-1">
                Suggestions isn't available on this server yet.
              </p>
            )}
            <p className="text-xs text-gray-500">
              {notDeployed
                ? 'The API for this feature needs deploying before scans can run.'
                : lastRun
                ? (lastRun.reason === 'no_notes'
                    ? 'No notes on this candidate yet.'
                    : 'Nothing new found in the notes.')
                  : 'No pending suggestions.'}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              {lastRun
                ? 'The profile already reflects what the notes say.'
                : 'Run a scan to check notes against the profile.'}
            </p>
          </div>
        ) : (
          suggestions.map(s => {
            const meta = WIDGET_META[s.widget] || { label: s.widget, badge: 'bg-gray-100 text-gray-600' };
            const isReview = s.action === 'review' || !s.field;
            const busy = busyId === s.id;
            return (
              <div key={s.id} className="border border-gray-200 rounded-lg p-2 hover:border-gray-300 transition-colors">
                {/* Card header */}
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${meta.badge}`}>
                    {meta.label}
                  </span>
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${CONFIDENCE_STYLES[s.confidence] || CONFIDENCE_STYLES.low}`}>
                    {s.confidence}
                  </span>
                </div>

                <p className="text-xs font-medium text-gray-900 leading-snug">{s.display_value}</p>

                {/* before → after, only for applyable field changes */}
                {!isReview && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[10px]">
                    <span className="px-1.5 py-0.5 bg-gray-50 text-gray-500 rounded line-through truncate max-w-[45%]">
                      {formatValue(s.current_value)}
                    </span>
                    <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-medium truncate max-w-[45%]">
                      {s.action === 'append' ? '+ ' : ''}{formatValue(s.suggested_value)}
                    </span>
                  </div>
                )}

                <p className="text-[10px] text-gray-500 mt-1.5 leading-snug">{s.rationale}</p>

                {s.evidence_quote && (
                  <div className="mt-1.5 pl-2 border-l-2 border-amber-200">
                    <p className="text-[10px] text-gray-600 italic leading-snug">"{s.evidence_quote}"</p>
                    {s.evidence_note_date && (
                      <p className="text-[9px] text-gray-400 mt-0.5">
                        Note from {new Date(s.evidence_note_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={() => resolve(s.id, 'accept')}
                    disabled={busy}
                    className="flex-1 py-1 text-[10px] font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-300"
                  >
                    {busy ? '…' : isReview ? 'Got it' : 'Apply'}
                  </button>
                  <button
                    onClick={() => resolve(s.id, 'dismiss')}
                    disabled={busy}
                    className="flex-1 py-1 text-[10px] font-medium rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>

                {isReview && (
                  <p className="text-[9px] text-gray-400 mt-1 leading-snug">
                    Update this in the {meta.label} widget — it can't be applied automatically.
                  </p>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SuggestionsPanel;
